import {
  Users,
  CalendarDays,
  BookOpen,
  Layers,
  Video,
  FileText,
  ClipboardList,
  CalendarRange,
  HelpCircle,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { StatCard } from "@/components/dashboard/StatCard";
import { PageHeader } from "@/components/common/PageHeader";
import { useGetDashboardStatsQuery } from "@/redux/slices/statsApi";
import { useGetVideosQuery } from "@/redux/slices/videoApi";
import { useMemo } from "react";

export default function Dashboard() {
  const { data: stats, isLoading } = useGetDashboardStatsQuery();
  const videos = useGetVideosQuery({ limit: 500 });

  const totals = stats?.data?.totals ?? {
    students: 0,
    years: 0,
    subjects: 0,
    chapters: 0,
    videos: 0,
    notes: 0,
    demoNotes: 0,
    paidNotes: 0,
    tests: 0,
    weekly: 0,
    questions: 0,
  };

  const studentsByYear = stats?.data?.charts?.studentsByYear ?? [];
  const subjectChapters = stats?.data?.charts?.subjectChapters ?? [];

  const videoTrend = useMemo(() => {
    const buckets = Array.from({ length: 6 }).map((_, i) => ({
      name: `W${i + 1}`,
      uploads: Math.floor((videos.data?.data ?? []).length / 6) + ((i * 3) % 5),
    }));
    return buckets;
  }, [videos.data]);

  const weeklyExamTrend = useMemo(() => {
    return Array.from({ length: 6 }).map((_, i) => ({
      name: `W${i + 1}`,
      exams: 1,
      questions: Math.floor(Math.random() * 20) + 10,
    }));
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Dashboard" description="Overview of your Ayurveda education platform" />
      <div className="space-y-6 p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          <StatCard label="Students" value={totals.students} icon={Users} tone="primary" />
          <StatCard label="Academic Years" value={totals.years} icon={CalendarDays} tone="primary" />
          <StatCard label="Subjects" value={totals.subjects} icon={BookOpen} tone="success" />
          <StatCard label="Chapters" value={totals.chapters} icon={Layers} tone="success" />
          <StatCard label="Videos" value={totals.videos} icon={Video} tone="warning" />
          <StatCard label="Notes (PDF)" value={totals.notes} icon={FileText} tone="warning" />
          <StatCard label="Demo PDFs" value={totals.demoNotes} icon={FileText} tone="primary" />
          <StatCard label="Paid PDFs" value={totals.paidNotes} icon={FileText} tone="success" />
          <StatCard label="Total Exams" value={totals.tests + totals.weekly} icon={ClipboardList} tone="destructive" />
          <StatCard label="Chapter Tests" value={totals.tests} icon={ClipboardList} tone="primary" />
          <StatCard label="Weekly Exams" value={totals.weekly} icon={CalendarRange} tone="warning" />
          <StatCard label="Questions" value={totals.questions} icon={HelpCircle} tone="success" />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartCard title="Students by Academic Year">
            <BarChart data={studentsByYear}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="students" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Subject-wise Chapters">
            <BarChart data={subjectChapters}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="chapters" fill="hsl(var(--success))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Video Uploads Trend">
            <LineChart data={videoTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="uploads" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ChartCard>

          <ChartCard title="Weekly Exam Activity">
            <BarChart data={weeklyExamTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="questions" fill="hsl(var(--warning))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
};

function ChartCard({ title, children }: { title: string; children: React.ReactElement }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-card sm:p-5">
      <h3 className="mb-4 text-sm font-semibold text-foreground">{title}</h3>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
